import {
  OrniTransportError,
  RemoteTerminalController,
  RemoteTerminalObserver,
  type RemoteTerminalControllerEvent,
  type RemoteTerminalControllerLease,
  type RemoteTerminalControllerState,
  type RemoteTerminalLeaseProof,
  type RemoteTerminalPrompt,
  type RemoteTerminalSnapshot,
  type RemoteTerminalUploadResult,
} from "../../sdk-typescript/src/index.ts";

export type Unsubscribe = () => void;

export interface ControllerState {
  mode: "available" | "owned" | "held-elsewhere" | "unavailable";
  holderLabel: string | null;
  leaseId: string | null;
  expiresAt: string | null;
  generation: number;
}

export interface TerminalScreenBootstrap {
  rows: number;
  columns: number;
  watermark: number;
  alternateScreen: boolean;
  applicationCursor: boolean;
  recentScrollbackText: string;
  data: Uint8Array;
  historyData?: Uint8Array;
  historyTruncated?: boolean;
  reset?: boolean;
}

export interface TerminalPromptEvent {
  id: string;
  title: string;
  detail: string;
  options: Array<{
    id: string;
    label: string;
    hotkey?: string;
    intent?: "allow_once" | "allow_scope" | "deny" | "answer" | "cancel" | "custom";
  }>;
}

export interface TerminalImageUploadInput {
  uploadId?: string;
  fileName: string;
  mimeType: string;
  bytes: Uint8Array;
}

export interface TerminalImageUploadResult {
  path: string;
  expiresAt: string | null;
  uploadId: string;
}

export interface TerminalConnection {
  readonly writable: boolean;
  readonly controller: ControllerState;
  readonly initialSnapshot: TerminalScreenBootstrap | null;
  readonly initialHistory: Uint8Array | null;
  readonly initialHistoryTruncated: boolean;
  readonly target?: { processAttemptId: string; generation: number };
  readonly initialSnapshotReady?: Promise<void>;
  onData(listener: (data: Uint8Array) => void): Unsubscribe;
  onSnapshot(listener: (snapshot: TerminalScreenBootstrap) => void): Unsubscribe;
  onPrompt(listener: (prompt: TerminalPromptEvent | null) => void): Unsubscribe;
  onClose(listener: () => void): Unsubscribe;
  onControlState(listener: (state: ControllerState) => void): Unsubscribe;
  /** Reports observer stream liveness independently of controller lease state. */
  onStreamState?(listener: (state: "connecting" | "connected" | "reconnecting" | "closed") => void): Unsubscribe;
  answerPrompt(promptId: string, optionId: string): void;
  requestControl(force?: boolean): Promise<ControllerState>;
  releaseControl(): Promise<ControllerState>;
  uploadImage(
    input: TerminalImageUploadInput,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
  ): Promise<TerminalImageUploadResult>;
  write(data: Uint8Array): void;
  resize(rows: number, columns: number): void;
  close(): void;
}

interface PendingLeaseRequest {
  action: "acquire" | "release";
  resolve: (state: ControllerState) => void;
  reject: (cause: Error) => void;
  timer: ReturnType<typeof setTimeout>;
}

export interface PendingRemoteTerminalOperation {
  operationId: string;
  kind: "input" | "resize";
  bytes?: Uint8Array;
  rows?: number;
  columns?: number;
  inFlight: boolean;
  /** Retry metadata stays with the operation, not with a socket attachment. */
  attempts?: number;
  nextRetryAt?: number;
}

/**
 * Session-scoped journal for writes which Cloud has not acknowledged yet.
 * It intentionally lives outside a WebSocket attachment: a relay/source
 * reconnect must not turn a submitted line into a silent loss.
 */
export class RemoteTerminalOperationJournal {
  private readonly operations = new Map<string, PendingRemoteTerminalOperation>();
  private totalBytes = 0;

  add(operation: Omit<PendingRemoteTerminalOperation, "inFlight">): void {
    if (this.operations.has(operation.operationId)) return;
    const bytes = operation.bytes?.byteLength ?? 0;
    if (this.operations.size >= 1_024 || this.totalBytes + bytes > 1024 * 1024) {
      throw new OrniTransportError(
        "UNAVAILABLE",
        "Remote terminal input is waiting for the computer to reconnect.",
        true,
      );
    }
    this.operations.set(operation.operationId, {
      ...operation,
      attempts: operation.attempts ?? 0,
      nextRetryAt: operation.nextRetryAt ?? 0,
      inFlight: false,
    });
    this.totalBytes += bytes;
  }

  acknowledge(operationId: string): void {
    const operation = this.operations.get(operationId);
    if (!operation) return;
    this.totalBytes -= operation.bytes?.byteLength ?? 0;
    this.operations.delete(operationId);
  }

  markUnsent(): void {
    for (const operation of this.operations.values()) operation.inFlight = false;
  }

  pending(): PendingRemoteTerminalOperation[] {
    return [...this.operations.values()];
  }

  eligible(now = Date.now()): PendingRemoteTerminalOperation[] {
    return this.pending().filter((operation) =>
      !operation.inFlight && (operation.nextRetryAt ?? 0) <= now,
    );
  }

  nextUnsentRetryAt(): number | null {
    const values = this.pending()
      .filter((operation) => !operation.inFlight)
      .map((operation) => operation.nextRetryAt ?? 0);
    return values.length ? Math.min(...values) : null;
  }

  deferUnsent(now = Date.now()): void {
    for (const operation of this.pending()) {
      if (!operation.inFlight) this.defer(operation.operationId, now);
    }
  }

  defer(operationId: string, now = Date.now()): void {
    const operation = this.operations.get(operationId);
    if (!operation) return;
    const attempts = (operation.attempts ?? 0) + 1;
    operation.attempts = attempts;
    operation.inFlight = false;
    operation.nextRetryAt = now + Math.min(30_000, 250 * (2 ** Math.min(attempts - 1, 7)));
  }

  markInFlight(operationId: string): void {
    const operation = this.operations.get(operationId);
    if (operation) operation.inFlight = true;
  }

  discardAll(): void {
    this.operations.clear();
    this.totalBytes = 0;
  }

  get size(): number { return this.operations.size; }
}

/** Shared adapter from the Cloud observe/control sockets to Desktop's terminal API. */
export class RemoteTerminalConnection implements TerminalConnection {
  // A relay may complete the WebSocket handshake and then lose the join
  // request without closing the socket. Bound that otherwise-unobservable
  // state so switching back to Terminal cannot wait forever.
  private static readonly INITIAL_SNAPSHOT_TIMEOUT_MS = 12_000;
  // A transient Cloud/Daemon outage must not turn a live terminal into a
  // permanent failed surface after an arbitrary number of retries. The
  // session cache owns idle disposal; this stream keeps retrying with
  // exponential backoff until the caller closes it or the relay reports a
  // non-recoverable ticket/policy condition.
  private static readonly MAX_OBSERVER_RECONNECT_ATTEMPTS = Number.POSITIVE_INFINITY;
  private static readonly PENDING_ATTACHMENT_ACK_TIMEOUT_MS = 30_000;
  private observer: RemoteTerminalObserver;
  // Unsubscribe/close cannot retract callbacks already queued by a WebSocket
  // implementation. Fence every observer attachment so a late frame from an
  // old process attempt cannot overwrite a newer snapshot.
  private observerAttachmentGeneration = 0;
  private observerUnsubscribe: Unsubscribe = () => undefined;
  private readonly reopenObserver: () => Promise<RemoteTerminalObserver>;
  private readonly openControl: (
    attachmentId: string,
    onEvent: (event: RemoteTerminalControllerEvent) => void,
  ) => Promise<RemoteTerminalController>;
  private readonly onDisposed: () => void;
  private readonly operationJournal: RemoteTerminalOperationJournal;
  private readonly attachmentId: string;
  private control: RemoteTerminalController | null = null;
  private lease: RemoteTerminalControllerLease | null = null;
  private pendingLease: PendingLeaseRequest | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  private operationRetryTimer: ReturnType<typeof setTimeout> | null = null;
  private controlAcquisition: Promise<ControllerState> | null = null;
  private controlReconnect: Promise<boolean> | null = null;
  private controlReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private controlReconnectAttempt = 0;
  private controlDesired = false;
  // A remote holder is a normal business state, not a transport failure. Do
  // not keep retrying the control socket until the user explicitly asks again.
  private controlBlockedByOther = false;
  private controllerStateEpoch = 0;
  private controllerStateRevision = 0;
  private observerReconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private observerReconnect: Promise<boolean> | null = null;
  private observerReconnectAttempt = 0;
  private observerHasJoined = false;
  private observerHasEverJoined = false;
  private observerTarget: { processAttemptId: string; generation: number } | null = null;
  private initialSnapshotTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly dataListeners = new Set<(data: Uint8Array) => void>();
  private readonly pendingData: Uint8Array[] = [];
  private pendingDataBytes = 0;
  private readonly snapshotListeners = new Set<(snapshot: TerminalScreenBootstrap) => void>();
  private readonly promptListeners = new Set<(prompt: TerminalPromptEvent | null) => void>();
  // The observer can receive the joined prompt before the page has finished
  // installing its React listener. Retain the authoritative prompt state so
  // a slow mount cannot strand the user behind a first-run Provider choice.
  private currentPrompt: TerminalPromptEvent | null = null;
  private readonly closeListeners = new Set<() => void>();
  private readonly controlListeners = new Set<(state: ControllerState) => void>();
  private readonly streamStateListeners = new Set<(state: "connecting" | "connected" | "reconnecting" | "closed") => void>();
  private readonly desynchronizedListeners = new Set<(message: string) => void>();
  private readonly initial: TerminalScreenBootstrap;
  private initialHistoryBytes: Uint8Array | null = null;
  private initialHistoryWasTruncated = false;
  private readonly initialSnapshotReadyPromise: Promise<void>;
  private resolveInitialSnapshotReady!: () => void;
  private initialSnapshotReadySettled = false;
  private closed = false;
  private controllerState: ControllerState = {
    mode: "available",
    holderLabel: null,
    leaseId: null,
    expiresAt: null,
    generation: 1,
  };
  private streamState: "connecting" | "connected" | "reconnecting" | "closed" = "connecting";
  private terminalFailureReasonState: string | null = null;
  private pendingAttachmentState: boolean;
  private pendingAttachmentConfirmedState: boolean;
  private pendingAttachmentAcknowledgedState: boolean;
  private pendingAttachmentAckTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingAttachResolution: {
    promise: Promise<"attached" | "waiting_for_pty">;
    resolve: (state: "attached" | "waiting_for_pty") => void;
    reject: (cause: Error) => void;
    settled: boolean;
  } | null = null;

  constructor(
    observer: RemoteTerminalObserver,
    openControl: (
      attachmentId: string,
      onEvent: (event: RemoteTerminalControllerEvent) => void,
    ) => Promise<RemoteTerminalController>,
    onDisposed: () => void = () => undefined,
    operationJournal = new RemoteTerminalOperationJournal(),
    reopenObserver: () => Promise<RemoteTerminalObserver> = async () => {
      throw new OrniTransportError("UNSUPPORTED", "Remote terminal observer cannot reconnect.");
    },
    attachmentId?: string,
  ) {
    const validatedObserver = requireRemoteTerminalObserver(observer);
    this.observer = validatedObserver;
    this.pendingAttachmentState = validatedObserver.pendingAttachment;
    this.pendingAttachmentConfirmedState = validatedObserver.pendingAttachmentConfirmed;
    this.pendingAttachmentAcknowledgedState = validatedObserver.pendingAttachmentAcknowledged;
    this.resetPendingAttachResolution();
    if (!this.pendingAttachmentState) this.resolvePendingAttachResolution("attached");
    else if (this.pendingAttachmentConfirmedState) this.resolvePendingAttachResolution("waiting_for_pty");
    this.openControl = openControl;
    this.reopenObserver = reopenObserver;
    this.attachmentId = attachmentId?.trim() || newClientId("attachment");
    this.onDisposed = onDisposed;
    this.operationJournal = operationJournal;
    this.operationJournal.markUnsent();
    this.initialSnapshotReadyPromise = new Promise((resolve) => {
      this.resolveInitialSnapshotReady = resolve;
    });
    this.initial = {
      rows: 24,
      columns: 80,
      watermark: 0,
      alternateScreen: false,
      applicationCursor: false,
      recentScrollbackText: "",
      data: new Uint8Array(),
      historyData: new Uint8Array(),
      historyTruncated: false,
    };
    this.attachObserver(validatedObserver);
  }

  private attachObserver(observer: RemoteTerminalObserver): void {
    observer = requireRemoteTerminalObserver(observer);
    const attachmentGeneration = ++this.observerAttachmentGeneration;
    const previousObserver = this.observer;
    const replacingObserver = previousObserver !== observer;
    if (replacingObserver) {
      // A replacement observer gets a fresh authoritative prompt state. Clear
      // the old value before the new ticket can report its prompt; otherwise a
      // resolved first-run selector could remain visible after reconnect.
      if (this.currentPrompt !== null) {
        this.currentPrompt = null;
        for (const listener of this.promptListeners) listener(null);
      }
      // A Cloud protocol-level `closed` frame does not guarantee that every
      // intermediary has already released the WebSocket. Close the previous
      // attachment before installing a fresh ticket so repeated reconnects do
      // not accumulate against the relay's per-Session capacity.
      this.observerUnsubscribe();
      this.observerUnsubscribe = () => undefined;
      previousObserver.close();
    }
    this.observer = observer;
    if (replacingObserver && observer.pendingAttachment) {
      // Attach resolution is scoped to one `open_session` acknowledgement.
      // A prior `waiting_for_pty` answer cannot authorize a lifecycle action
      // after that observer is gone; wait for the replacement stream's own
      // authoritative pending/bound frame.
      this.resetPendingAttachResolution();
    }
    // A replacement observer represents a new pending-attach handshake.
    // Never carry an acknowledgement from the previous socket over to it:
    // otherwise a lost open_session frame could leave this connection waiting
    // forever without either a timeout or a retry.
    this.pendingAttachmentState = observer.pendingAttachment;
    this.pendingAttachmentConfirmedState = observer.pendingAttachmentConfirmed;
    this.pendingAttachmentAcknowledgedState = observer.pendingAttachmentAcknowledged;
    if (!replacingObserver) this.observerUnsubscribe();
    this.observerUnsubscribe = observer.subscribe((event) => {
      if (this.closed || this.observer !== observer
        || attachmentGeneration !== this.observerAttachmentGeneration) return;
      if (event.type === "joined") {
        this.pendingAttachmentState = false;
        this.pendingAttachmentConfirmedState = false;
        this.pendingAttachmentAcknowledgedState = true;
        this.stopPendingAttachmentAckWatchdog();
        this.resolvePendingAttachResolution("attached");
        const reconnecting = this.observerHasEverJoined;
        const nextTarget = {
          processAttemptId: event.snapshot.target.process_attempt_id,
          generation: event.snapshot.target.generation,
        };
        const targetChanged = this.observerTarget !== null && (
          this.observerTarget.processAttemptId !== nextTarget.processAttemptId
          || this.observerTarget.generation !== nextTarget.generation
        );
        this.observerTarget = nextTarget;
        this.observerHasEverJoined = true;
        this.observerHasJoined = true;
        this.updateStreamState("connected");
        this.observerReconnectAttempt = 0;
        if (this.observerReconnectTimer !== null) {
          clearTimeout(this.observerReconnectTimer);
          this.observerReconnectTimer = null;
        }
        const history = event.snapshot.history;
        this.initialHistoryBytes = null;
        this.initial.historyData = new Uint8Array();
        if (history?.frames.length) {
          const byteLength = history.frames.reduce((total, frame) => total + frame.bytes.length, 0);
          const bytes = new Uint8Array(byteLength);
          let offset = 0;
          for (const frame of history.frames) {
            bytes.set(frame.bytes, offset);
            offset += frame.bytes.length;
          }
          this.initialHistoryBytes = bytes;
          this.initial.historyData = bytes;
        }
        this.initialHistoryWasTruncated = history?.truncated === true || history?.has_earlier === true;
        this.initial.historyTruncated = this.initialHistoryWasTruncated;
        this.initial.rows = event.snapshot.rows;
        this.initial.columns = event.snapshot.columns;
        this.initial.watermark = event.snapshot.watermark;
        this.initial.alternateScreen = event.snapshot.active_screen === "alternate";
        this.initial.applicationCursor = detectApplicationCursorMode(event.snapshot.vt_bootstrap)
          ?? (event.snapshot.modes?.application_cursor === true);
        this.controllerState = {
          ...this.controllerState,
          generation: event.snapshot.target.generation,
        };
        if (targetChanged) this.invalidateControlForTargetChange();
        this.initial.recentScrollbackText = event.snapshot.scrollback_text
          || linesText(event.snapshot.scrollback);
        // Prefer the exact parser bootstrap. It restores alternate-screen
        // state, attributes, cursor and erase sequences without replaying
        // plain screen text as if it were PTY input. The structured fallback
        // keeps older Daemons usable until they are updated.
        const nextData = event.snapshot.vt_bootstrap.length
          ? Uint8Array.from(event.snapshot.vt_bootstrap)
          : structuredSnapshotBootstrap(event.snapshot);
        // During stop/restart a relay can briefly join with dimensions and
        // target metadata but no screen payload. Keep the last authoritative
        // screen until a real bootstrap arrives; emitting an empty reset here
        // makes every client clear a perfectly good terminal.
        const preserveExistingScreen = reconnecting
          && this.initial.data.byteLength > 0
          && isEmptyRemoteTerminalSnapshot(event.snapshot);
        if (!preserveExistingScreen) {
          // A joined frame is a complete VT replacement through its
          // watermark. Bytes retained while no page listener was mounted
          // belong to the old snapshot/stream and must not be replayed on top
          // of this bootstrap. Output after this point is queued normally.
          this.pendingData.length = 0;
          this.pendingDataBytes = 0;
          this.initial.data = nextData;
        }
        this.initial.reset = reconnecting;
        this.stopInitialSnapshotWatchdog();
        this.resolveInitialSnapshotReadyOnce();
        for (const listener of this.snapshotListeners) listener(this.initial);
      } else if (event.type === "controller_state") {
        this.applyControllerState(event.state);
      } else if (event.type === "pending") {
        const previouslyConfirmedStopped = this.pendingAttachmentConfirmedState;
        this.pendingAttachmentState = event.status === "waiting_for_pty" || event.status === "starting";
        this.pendingAttachmentConfirmedState = event.status === "waiting_for_pty";
        this.pendingAttachmentAcknowledgedState = true;
        this.stopPendingAttachmentAckWatchdog();
        // A fixed-device stream can be subscribed before a PTY exists. This
        // is a stable waiting state, not a transport failure: keep the
        // attachment alive until the Daemon emits pty_bound and a snapshot.
        if (this.pendingAttachmentState) this.updateStreamState("connecting");
        if (event.status === "waiting_for_pty") this.resolvePendingAttachResolution("waiting_for_pty");
        else if (event.status === "starting" && previouslyConfirmedStopped) this.resetPendingAttachResolution();
        else if (!this.pendingAttachmentState) {
          this.rejectPendingAttachResolution(new OrniTransportError("UNAVAILABLE", `Remote terminal attachment rejected: ${event.status}.`));
        }
        this.stopInitialSnapshotWatchdog();
        this.observerHasJoined = false;
      } else if (event.type === "pty_bound") {
        this.observerTarget = {
          processAttemptId: event.target.process_attempt_id,
          generation: event.target.generation,
        };
        this.pendingAttachmentState = false;
        this.pendingAttachmentConfirmedState = false;
        this.pendingAttachmentAcknowledgedState = true;
        this.stopPendingAttachmentAckWatchdog();
        this.resolvePendingAttachResolution("attached");
        // A pending fixed-device stream intentionally has no initial-screen
        // deadline while it waits for a PTY. Once the Daemon binds an active
        // attempt, restore the normal snapshot watchdog so a lost bind or
        // snapshot cannot leave the UI connecting forever.
        this.scheduleInitialSnapshotWatchdog();
      } else if (event.type === "pty_unbound") {
        if (this.observerTarget
          && this.observerTarget.processAttemptId === event.target.process_attempt_id
          && this.observerTarget.generation === event.target.generation) {
          this.observerTarget = null;
          this.pendingAttachmentState = true;
          this.pendingAttachmentConfirmedState = false;
          this.pendingAttachmentAcknowledgedState = true;
          this.stopPendingAttachmentAckWatchdog();
          this.resetPendingAttachResolution();
          this.invalidateControlForTargetChange();
          this.updateStreamState("connecting");
        }
      } else if (event.type === "output") {
        const bytes = Uint8Array.from(event.frame.bytes);
        if (this.dataListeners.size === 0) {
          this.pendingData.push(bytes);
          this.pendingDataBytes += bytes.byteLength;
          while (this.pendingDataBytes > 512 * 1024 && this.pendingData.length > 1) {
            this.pendingDataBytes -= this.pendingData.shift()?.byteLength ?? 0;
          }
        } else {
          for (const listener of this.dataListeners) listener(bytes);
        }
      } else if (event.type === "gap") {
        // A terminal gap means the relay can no longer replay a contiguous
        // stream. The only safe recovery is a fresh observer ticket and
        // snapshot; never guess or replay input across a fenced target.
        const message = "Remote terminal output fell behind; reconnecting from a fresh snapshot.";
        for (const listener of this.desynchronizedListeners) listener(message);
        this.handleObserverLoss(message, event.gap.target.generation);
      } else if (event.type === "prompt") {
        this.currentPrompt = mapRemotePrompt(event.prompt);
        for (const listener of this.promptListeners) listener(this.currentPrompt);
      } else if (event.type === "prompt_resolved") {
        this.currentPrompt = null;
        for (const listener of this.promptListeners) listener(null);
      } else if (event.type === "closed") {
        this.handleObserverLoss(
          event.reason,
          this.controllerState.generation,
          isRecoverableObserverCloseReason(event.reason),
        );
      }
    });
    this.scheduleInitialSnapshotWatchdog();
    this.schedulePendingAttachmentAckWatchdog();
  }

  get writable(): boolean { return this.controllerState.mode === "owned" && this.lease !== null; }
  get pendingAttachment(): boolean { return this.pendingAttachmentState; }
  get pendingAttachmentConfirmed(): boolean { return this.pendingAttachmentConfirmedState; }
  get pendingAttachmentAcknowledged(): boolean { return this.pendingAttachmentAcknowledgedState; }
  get terminalFailureReason(): string | null { return this.terminalFailureReasonState; }
  waitForPendingAttachResolution(): Promise<"attached" | "waiting_for_pty"> {
    if (!this.pendingAttachmentState) return Promise.resolve("attached");
    if (this.pendingAttachmentConfirmedState) return Promise.resolve("waiting_for_pty");
    if (!this.pendingAttachResolution) this.resetPendingAttachResolution();
    return this.pendingAttachResolution!.promise;
  }
  get controller(): ControllerState { return { ...this.controllerState }; }
  /** True once this attachment has received a complete authoritative screen. */
  get hasAuthoritativeSnapshot(): boolean {
    return this.observerHasEverJoined && this.observerTarget !== null && !this.pendingAttachmentState;
  }
  /** Observer liveness is independent from whether the last screen is reusable. */
  get streamStatus(): "connecting" | "connected" | "reconnecting" | "closed" {
    return this.streamState;
  }
  get initialSnapshot(): TerminalScreenBootstrap | null {
    // `initialSnapshotReady` is intentionally always settled when an
    // attachment closes before its first `joined` frame so callers cannot be
    // stranded forever in a loading state. In that terminal case there is no
    // authoritative screen to paint; expose null instead of the constructor's
    // empty 24x80 placeholder. A caller can then render the stream error (or
    // an explicit reconnect action) without issuing a control request against
    // a closed observer.
    if (this.closed && !this.observerHasEverJoined) return null;
    return {
      ...this.initial,
      data: this.initial.data.slice(),
      historyData: this.initial.historyData?.slice(),
    };
  }
  get initialSnapshotReady(): Promise<void> { return this.initialSnapshotReadyPromise; }
  get initialHistory(): Uint8Array | null {
    return this.initialHistoryBytes?.slice() ?? null;
  }
  get initialHistoryTruncated(): boolean { return this.initialHistoryWasTruncated; }
  get target(): { processAttemptId: string; generation: number } | undefined {
    return this.observerTarget ? { ...this.observerTarget } : undefined;
  }
  onData(listener: (data: Uint8Array) => void): Unsubscribe {
    this.dataListeners.add(listener);
    if (this.pendingData.length) {
      const pending = this.pendingData.splice(0);
      this.pendingDataBytes = 0;
      for (const data of pending) listener(data);
    }
    return () => this.dataListeners.delete(listener);
  }
  onSnapshot(listener: (snapshot: TerminalScreenBootstrap) => void): Unsubscribe { this.snapshotListeners.add(listener); return () => this.snapshotListeners.delete(listener); }
  onPrompt(listener: (prompt: TerminalPromptEvent | null) => void): Unsubscribe {
    this.promptListeners.add(listener);
    listener(this.currentPrompt);
    return () => this.promptListeners.delete(listener);
  }
  onClose(listener: () => void): Unsubscribe { if (this.closed) queueMicrotask(listener); else this.closeListeners.add(listener); return () => this.closeListeners.delete(listener); }
  onControlState(listener: (state: ControllerState) => void): Unsubscribe { this.controlListeners.add(listener); listener(this.controller); return () => this.controlListeners.delete(listener); }
  onStreamState(listener: (state: "connecting" | "connected" | "reconnecting" | "closed") => void): Unsubscribe { this.streamStateListeners.add(listener); listener(this.streamState); return () => this.streamStateListeners.delete(listener); }
  onDesynchronized(listener: (message: string) => void): Unsubscribe { this.desynchronizedListeners.add(listener); return () => this.desynchronizedListeners.delete(listener); }
  answerPrompt(promptId: string, optionId: string): void {
    if (!this.control || !this.lease || !this.writable) {
      throw new OrniTransportError("CONFLICT", "Terminal input is not ready for this prompt.", true);
    }
    this.control.answerPrompt(leaseProof(this.lease), promptId, optionId);
  }
  async requestControl(force = false): Promise<ControllerState> {
    if (this.closed) throw new OrniTransportError("UNAVAILABLE", "The remote terminal is closed.", true);
    this.controlDesired = true;
    this.controlBlockedByOther = false;
    if (this.writable) {
      remoteTerminalDiagnostic("control.already_owned", {
        attachmentId: this.attachmentId,
        force,
        generation: this.controllerState.generation,
      });
      return this.controller;
    }
    if (this.controlAcquisition) {
      remoteTerminalDiagnostic("control.join_in_flight", {
        attachmentId: this.attachmentId,
        force,
        generation: this.controllerState.generation,
      });
      return this.controlAcquisition;
    }
    remoteTerminalDiagnostic("control.begin", {
      attachmentId: this.attachmentId,
      force,
      generation: this.controllerState.generation,
    });
    const acquisition = (async () => {
      const control = await this.ensureControl();
      if (this.writable) return this.controller;
      return this.beginLeaseRequest("acquire", () => {
        control.lease({
          operationId: newClientId("lease"),
          action: "acquire",
          force,
        });
      });
    })();
    this.controlAcquisition = acquisition;
    try {
      return await acquisition;
    } finally {
      if (this.controlAcquisition === acquisition) this.controlAcquisition = null;
      remoteTerminalDiagnostic("control.end", {
        attachmentId: this.attachmentId,
        force,
        mode: this.controllerState.mode,
        generation: this.controllerState.generation,
      });
    }
  }
  async releaseControl(): Promise<ControllerState> {
    this.controlDesired = false;
    this.controlBlockedByOther = false;
    if (this.controlReconnectTimer !== null) clearTimeout(this.controlReconnectTimer);
    this.controlReconnectTimer = null;
    if (!this.lease || !this.control) return this.controller;
    const lease = this.lease;
    this.stopRenewal();
    return this.beginLeaseRequest("release", () => {
      this.control?.lease({
        operationId: newClientId("lease"),
        action: "release",
        proof: leaseProof(lease),
        expectedRevision: lease.revision,
      });
    });
  }
  async uploadImage(
    input: TerminalImageUploadInput,
    onProgress?: (uploadedBytes: number, totalBytes: number) => void,
  ): Promise<TerminalImageUploadResult> {
    if (this.closed) throw new OrniTransportError("UNAVAILABLE", "The remote terminal is closed.", true);
    if (!this.writable || !this.lease || !this.control) {
      // Replay may run in the background after a relay reconnect. Recover an
      // idle lease, but never take control away from another device without an
      // explicit user action.
      await this.requestControl(false);
    }
    const control = this.control;
    const lease = this.lease;
    if (!control || !lease || !this.writable) {
      throw new OrniTransportError("CONFLICT", "Terminal input is not ready for image upload.", true);
    }
    const result: RemoteTerminalUploadResult = await control.uploadImage(
      leaseProof(lease),
      input,
      onProgress,
    );
    if (!result.path) {
      throw new OrniTransportError("UNAVAILABLE", "The computer did not return a staged image path.", true);
    }
    return {
      path: result.path,
      expiresAt: result.expires_at ?? null,
      uploadId: result.upload_id,
    };
  }
  write(data: Uint8Array): void {
    if (!this.control || !this.lease || !this.writable) {
      throw new OrniTransportError("CONFLICT", "Terminal input is not ready.", true);
    }
    const operationId = newClientId("terminal-input");
    this.operationJournal.add({
      operationId,
      kind: "input",
      bytes: data.slice(),
    });
    try {
      this.sendJournalOperation(this.operationJournal.pending().find((item) => item.operationId === operationId)!);
    } catch (cause) {
      // The operation has not received a durable relay acknowledgement. Keep
      // it in the session journal so a replacement control socket can replay
      // the same idempotent operation after a transient network failure.
      this.operationJournal.defer(operationId);
      this.scheduleOperationReplay();
      throw cause;
    }
  }
  resize(rows: number, columns: number): void {
    if (!this.control || !this.lease || !this.writable) return;
    const operationId = newClientId("terminal-resize");
    this.operationJournal.add({
      operationId,
      kind: "resize",
      rows,
      columns,
    });
    try {
      this.sendJournalOperation(this.operationJournal.pending().find((item) => item.operationId === operationId)!);
    } catch (cause) {
      this.operationJournal.defer(operationId);
      this.scheduleOperationReplay();
      throw cause;
    }
  }
  close(): void {
    if (this.closed) return;
    this.closed = true;
    // Invalidate callbacks queued before the observer was closed.
    this.observerAttachmentGeneration += 1;
    this.updateStreamState("closed");
    this.controlDesired = false;
    this.controlBlockedByOther = false;
    this.stopInitialSnapshotWatchdog();
    this.stopPendingAttachmentAckWatchdog();
    // A caller can leave the page while the first snapshot is still in
    // flight. Resolve only this explicit disposal path so the awaiting UI can
    // finish its cancelled branch; transient network loss remains pending
    // and is recovered by the observer reconnect path.
    this.resolveInitialSnapshotReadyOnce();
    this.stopRenewal();
    if (this.operationRetryTimer !== null) clearTimeout(this.operationRetryTimer);
    this.operationRetryTimer = null;
    if (this.controlReconnectTimer !== null) clearTimeout(this.controlReconnectTimer);
    this.controlReconnectTimer = null;
    if (this.observerReconnectTimer !== null) clearTimeout(this.observerReconnectTimer);
    this.observerReconnectTimer = null;
    this.observerUnsubscribe();
    this.observerUnsubscribe = () => undefined;
    if (this.control && this.lease) {
      try {
        this.control.lease({
          operationId: newClientId("lease"),
          action: "release",
          proof: leaseProof(this.lease),
          expectedRevision: this.lease.revision,
        });
      } catch {
        // The Cloud lease is short-lived and will expire if the socket already closed.
      }
    }
    this.rejectPending(new OrniTransportError("UNAVAILABLE", "The remote terminal is closed.", true));
    this.rejectPendingAttachResolution(new OrniTransportError("UNAVAILABLE", "The remote terminal is closed.", true));
    this.operationJournal.markUnsent();
    this.control?.close();
    this.control = null;
    this.lease = null;
    this.observer.close();
    this.currentPrompt = null;
    this.onDisposed();
  }

  /**
   * Re-establish a foreground attachment after a native app was suspended.
   * iOS and Android may freeze a WebSocket without delivering an onclose
   * event. Reusing that socket leaves the terminal looking connected while
   * output and control frames are no longer flowing, so deliberately rotate
   * both observer and control attachments when the app returns to foreground.
   */
  resume(): void {
    if (this.closed) return;
    const generation = this.observerTarget?.generation ?? this.controllerState.generation;
    // The observer may still be in its first handshake, so checking only
    // `observerHasJoined` would miss a suspended pre-join socket. Rotating the
    // observer is idempotent: an existing reconnect timer/promise remains the
    // single in-flight recovery path.
    this.refreshSnapshot("Remote terminal foreground resume.");
    const staleControl = this.control;
    if (!staleControl) return;
    this.control = null;
    this.lease = null;
    this.stopRenewal();
    this.operationJournal.markUnsent();
    staleControl.close();
    this.updateController({
      mode: this.controllerState.mode === "held-elsewhere" ? "held-elsewhere" : "unavailable",
      holderLabel: this.controllerState.mode === "held-elsewhere" ? this.controllerState.holderLabel : null,
      leaseId: null,
      expiresAt: null,
      generation: this.controllerState.generation,
    });
    if (this.controlDesired) this.scheduleControlReconnect();
  }

  /**
   * Rejoin the observer without touching the input lease. A page can be
   * unmounted while its session connection remains cached; on the next mount
   * the cached screen may be behind the Daemon even though the socket is still
   * alive. Rotating only the observer asks the authority for a new snapshot
   * while preserving whichever device currently owns input.
   */
  refreshSnapshot(reason = "Remote terminal snapshot refresh."): void {
    if (this.closed) return;
    const generation = this.observerTarget?.generation ?? this.controllerState.generation;
    this.handleObserverLoss(reason, generation, true);
  }

  private async ensureControl(): Promise<RemoteTerminalController> {
    if (this.control) return this.control;
    remoteTerminalDiagnostic("control.socket_open.begin", {
      attachmentId: this.attachmentId,
      generation: this.controllerState.generation,
    });
    try {
      let opened: RemoteTerminalController | null = null;
      const earlyEvents: RemoteTerminalControllerEvent[] = [];
      const control = await this.openControl(this.attachmentId, (event) => {
        if (opened) this.handleControlEvent(opened, event);
        else earlyEvents.push(event);
      });
      opened = control;
      if (this.closed) {
        control.close();
        throw new OrniTransportError("UNAVAILABLE", "The remote terminal is closed.", true);
      }
      this.control = control;
      // Multiplex transports may deliver control_ready/controller_state before
      // openControl resolves its handshake promise. Replay those events after
      // the connection is installed so the initial ownership projection is
      // never lost to promise timing.
      for (const event of earlyEvents) this.handleControlEvent(control, event);
      // Reopening the control socket is a transport event, not an ownership
      // decision. Preserve a known remote holder until the next lease result
      // so reconnects do not flash the UI back to an apparently available
      // state.
      this.updateController({
        ...this.controllerState,
        // A control socket is not authoritative until it returns a fresh
        // controller_state. Do not present a transient reconnect as an
        // available lease that the user can race.
        mode: this.controllerState.mode === "held-elsewhere" ? "held-elsewhere" : "unavailable",
        holderLabel: this.controllerState.mode === "held-elsewhere" ? this.controllerState.holderLabel : null,
        leaseId: null,
        expiresAt: null,
      });
      remoteTerminalDiagnostic("control.socket_open.ready", {
        attachmentId: this.attachmentId,
        generation: this.controllerState.generation,
      });
      return control;
    } catch (cause) {
      this.updateController({ ...this.controllerState, mode: "unavailable", leaseId: null, expiresAt: null });
      remoteTerminalDiagnostic("control.socket_open.failed", {
        attachmentId: this.attachmentId,
        generation: this.controllerState.generation,
        error: cause instanceof Error ? cause.message : String(cause),
      });
      throw cause;
    }
  }

  private beginLeaseRequest(
    action: PendingLeaseRequest["action"],
    send: () => void,
  ): Promise<ControllerState> {
    if (this.pendingLease) {
      remoteTerminalDiagnostic("lease.duplicate_rejected", {
        attachmentId: this.attachmentId,
        action,
        generation: this.controllerState.generation,
      });
      return Promise.reject(new OrniTransportError("CONFLICT", "A terminal control request is already pending.", true));
    }
    remoteTerminalDiagnostic("lease.begin", {
      attachmentId: this.attachmentId,
      action,
      generation: this.controllerState.generation,
    });
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        if (this.pendingLease?.timer !== timer) return;
        this.pendingLease = null;
        reject(new OrniTransportError("TIMEOUT", "The remote terminal control request timed out.", true));
      }, 10_000);
      this.pendingLease = { action, resolve, reject, timer };
      try {
        send();
      } catch (cause) {
        this.rejectPending(asError(cause));
      }
    });
  }

  private handleControlEvent(
    source: RemoteTerminalController,
    event: RemoteTerminalControllerEvent,
  ): void {
    // A socket can report both the protocol-level `closed` message and the
    // browser's later `onclose`. Once a replacement is installed, delayed
    // callbacks from the old socket must not clear or fence the new one.
    if (source !== this.control) return;
    if (event.type === "controller_state") {
      this.applyControllerState(event.state);
      return;
    }
    if (event.type === "lease") {
      const pendingAction = this.pendingLease?.action;
      if (event.lease.status === "active") {
        this.lease = event.lease;
        this.updateController({
          mode: "owned",
          holderLabel: null,
          leaseId: event.lease.lease_id,
          expiresAt: event.lease.expires_at,
          generation: event.lease.target.generation,
        });
        this.scheduleRenewal();
        this.replayPendingOperations();
      } else {
        this.lease = null;
        this.stopRenewal();
        const heldElsewhere = this.controllerState.mode === "held-elsewhere";
        this.updateController({
          mode: heldElsewhere ? "held-elsewhere" : "available",
          holderLabel: heldElsewhere ? this.controllerState.holderLabel : null,
          leaseId: null,
          expiresAt: heldElsewhere ? this.controllerState.expiresAt : null,
          generation: event.lease.target.generation,
        });
      }
      if ((event.lease.status === "active" && pendingAction === "acquire")
        || (event.lease.status !== "active" && pendingAction === "release")) {
        this.resolvePending();
      }
      if (event.lease.status !== "active" && this.controlDesired && !this.controlBlockedByOther && !this.pendingLease) {
        this.scheduleControlReconnect();
      }
      return;
    }
    if (event.type === "result") {
      const operationId = event.result.operation_id;
      if (!operationId) return;
      const pending = this.operationJournal.pending().find((item) => item.operationId === operationId);
      if (!pending) return;
      if (event.result.outcome === "applied") {
        this.operationJournal.acknowledge(operationId);
        return;
      }
      // A rejection can be caused by a source/lease rotation. Never recurse
      // synchronously here: doing that turns one transient rejection into a
      // tight resend loop and makes the browser look permanently disconnected.
      // Keep the same operation id and retry with bounded exponential backoff.
      if (event.result.outcome === "lease_rejected") {
        // The source has positively fenced this proof. Retrying the same
        // operation would replay input after another device took control.
        this.operationJournal.acknowledge(operationId);
        this.invalidateLeaseAfterFence("remote terminal lease was rejected");
        return;
      }
      if (event.result.outcome === "rejected") {
        this.operationJournal.defer(operationId);
        this.scheduleOperationReplay();
        return;
      }
      // A superseded process attempt cannot safely receive bytes from the old
      // PTY. Remove it from the automatic queue; the UI can send the text
      // again to the new attempt explicitly.
      this.operationJournal.acknowledge(operationId);
      if (event.result.outcome === "attempt_superseded") {
        this.invalidateControlForTargetChange();
      }
      return;
    }
    if (event.type === "error") {
      const leaseFenced = isRemoteTerminalLeaseFenceReason(event.reason);
      if (this.pendingLease) {
        this.rejectPending(new OrniTransportError("CONFLICT", event.reason, true));
        if (leaseFenced || isRemoteTerminalHeldElsewhereReason(event.reason)) {
          // A normal acquire can be rejected because another attachment is
          // still active. This is a settled control state, not a transport
          // failure: retain the observer and expose the explicit takeover
          // action instead of leaving the UI looking available.
          this.invalidateLeaseAfterFence(event.reason);
        }
      } else if (event.requestId !== null) {
        if (leaseFenced) {
          this.invalidateLeaseAfterFence(event.reason);
          return;
        }
        // A relay command timeout is scoped to one input/resize request. It
        // must not revoke an otherwise healthy lease: doing so made one lost
        // keystroke tear down the whole terminal and start a reconnect loop.
        this.operationJournal.markUnsent();
        this.operationJournal.deferUnsent();
        this.scheduleOperationReplay();
      } else if (this.lease) {
        // A failed renewal no longer proves control ownership. Keep the
        // observer alive, but require an explicit fresh acquisition before
        // accepting any more input.
        this.lease = null;
        this.stopRenewal();
        this.updateController({
          mode: "unavailable",
          holderLabel: null,
          leaseId: null,
          expiresAt: null,
          generation: this.controllerState.generation,
        });
        if (this.controlDesired && !this.controlBlockedByOther) this.scheduleControlReconnect();
      }
      return;
    }
    if (event.type === "closed") {
      const heldElsewhere = isRemoteTerminalHeldElsewhereReason(event.reason ?? "");
      this.control = null;
      this.lease = null;
      this.operationJournal.markUnsent();
      this.operationJournal.deferUnsent();
      this.stopRenewal();
      this.rejectPending(new OrniTransportError("UNAVAILABLE", event.reason || "Remote terminal control closed.", true));
      this.updateController({
        mode: this.closed
          ? "unavailable"
          : heldElsewhere || this.controllerState.mode === "held-elsewhere" ? "held-elsewhere" : "unavailable",
        holderLabel: heldElsewhere || this.controllerState.mode === "held-elsewhere" ? this.controllerState.holderLabel : null,
        leaseId: null,
        expiresAt: null,
        generation: this.controllerState.generation,
      });
      if (heldElsewhere) {
        this.controlBlockedByOther = true;
        this.controlDesired = false;
        if (this.controlReconnectTimer !== null) clearTimeout(this.controlReconnectTimer);
        this.controlReconnectTimer = null;
      }
      if (!this.closed && !heldElsewhere && (this.controlDesired || this.operationJournal.size > 0)) {
        this.scheduleControlReconnect();
      }
    }
  }

  private scheduleRenewal(): void {
    this.stopRenewal();
    const lease = this.lease;
    if (!lease) return;
    const renewedAt = Date.parse(lease.renewed_at);
    const expiresAt = Date.parse(lease.expires_at);
    const ttlMs = expiresAt - renewedAt;
    const remainingMs = expiresAt - Date.now();
    // Derive the renewal point from the server's lease window. The cap only
    // bounds a malformed/very long server lease; it is not the lease policy.
    const renewalDelay = ttlMs > 0 && remainingMs > 0
      ? Math.max(250, Math.min(Math.floor(ttlMs / 2), Math.floor(remainingMs / 2), 60_000))
      : 250;
    this.renewalTimer = setTimeout(() => {
      this.renewalTimer = null;
      const currentLease = this.lease;
      const currentControl = this.control;
      if (!currentLease || !currentControl || this.closed || this.pendingLease) return;
      try {
        currentControl.lease({
          operationId: newClientId("lease"),
          action: "renew",
          proof: leaseProof(currentLease),
          expectedRevision: currentLease.revision,
        });
      } catch (cause) {
        this.handleControlEvent(currentControl, { type: "error", requestId: null, reason: asError(cause).message });
      }
    }, renewalDelay);
  }

  private handleObserverLoss(_reason: string, generation: number, recoverable = true): void {
    if (this.closed) return;
    // Invalidate the current attachment before closing it. WebSocket
    // implementations may dispatch already-queued frames after unsubscribe;
    // those frames belong to the failed target and must not revive it while a
    // replacement ticket is being acquired.
    this.observerAttachmentGeneration += 1;
    this.observerHasJoined = false;
    this.updateStreamState(recoverable ? "reconnecting" : "closed");
    this.stopInitialSnapshotWatchdog();
    // A relay can close while its first `joined` frame is still in flight.
    // Keep the session-scoped connection alive and acquire a fresh observer
    // in exactly the same way as an established stream. Rejecting the
    // initialSnapshotReady promise here permanently strands the TerminalSurface
    // in its loading state when the user switches views during that window.
    // A fresh observer always starts from an authoritative snapshot, so a
    // stream gap is recoverable without replaying or guessing missing bytes.
    if (recoverable) {
      this.observerUnsubscribe();
      this.observerUnsubscribe = () => undefined;
      // Release the failed attachment immediately. `reopenObserver` always
      // obtains a one-use ticket and refreshes the process-attempt target.
      this.observer.close();
      this.scheduleObserverReconnect();
      return;
    }
    this.terminalFailureReasonState = _reason || "The remote terminal is unavailable.";
    this.rejectPendingAttachResolution(new OrniTransportError("UNAVAILABLE", _reason || "The remote terminal is unavailable."));
    this.closed = true;
    // Unblock a page waiting for its first snapshot when the relay reports a
    // terminal, non-recoverable close. The page can then render the explicit
    // error instead of remaining in a permanent loading state.
    this.resolveInitialSnapshotReadyOnce();
    this.stopRenewal();
    this.control?.close();
    this.control = null;
    this.lease = null;
    this.updateController({
      mode: "unavailable",
      holderLabel: null,
      leaseId: null,
      expiresAt: null,
      generation,
    });
    this.observer.close();
    for (const listener of this.closeListeners) listener();
    this.onDisposed();
  }

  private scheduleObserverReconnect(): void {
    if (this.closed || this.observerReconnectTimer !== null || this.observerReconnect) return;
    if (this.observerReconnectAttempt >= RemoteTerminalConnection.MAX_OBSERVER_RECONNECT_ATTEMPTS) {
      this.handleObserverLoss(
        "Remote terminal stream could not reconnect after several attempts.",
        this.controllerState.generation,
        false,
      );
      return;
    }
    const delay = Math.min(8_000, 500 * (2 ** Math.min(this.observerReconnectAttempt, 4)));
    this.observerReconnectAttempt += 1;
    this.observerReconnectTimer = setTimeout(() => {
      this.observerReconnectTimer = null;
      const task = this.reconnectObserver();
      this.observerReconnect = task;
      void task.then((opened) => {
        if (this.observerReconnect !== task) return;
        this.observerReconnect = null;
        // A failed ticket refresh must schedule after the in-flight fence has
        // been released. Scheduling from inside reconnectObserver used to be
        // ignored because observerReconnect still pointed at this task.
        if (!opened && !this.closed && !this.observerHasJoined) this.scheduleObserverReconnect();
      });
    }, delay);
  }

  private scheduleInitialSnapshotWatchdog(): void {
    this.stopInitialSnapshotWatchdog();
    // Pending attachment is a valid long-lived state: the Session may be
    // stopped or still starting, and the fixed relay stream must remain open
    // until the Daemon emits `pty_bound`. The normal initial-screen deadline
    // starts only after a PTY is bound.
    if (this.closed || this.observerHasJoined || this.pendingAttachmentState) return;
    this.initialSnapshotTimer = setTimeout(() => {
      this.initialSnapshotTimer = null;
      if (this.closed || this.observerHasJoined || this.pendingAttachmentState) return;
      this.handleObserverLoss(
        "Remote terminal relay did not send its initial snapshot; reconnecting.",
        this.controllerState.generation,
      );
    }, RemoteTerminalConnection.INITIAL_SNAPSHOT_TIMEOUT_MS);
  }

  private stopInitialSnapshotWatchdog(): void {
    if (this.initialSnapshotTimer !== null) clearTimeout(this.initialSnapshotTimer);
    this.initialSnapshotTimer = null;
  }

  private schedulePendingAttachmentAckWatchdog(): void {
    this.stopPendingAttachmentAckWatchdog();
    if (this.closed || !this.pendingAttachmentState || this.pendingAttachmentAcknowledgedState) return;
    this.pendingAttachmentAckTimer = setTimeout(() => {
      this.pendingAttachmentAckTimer = null;
      if (this.closed || this.pendingAttachmentAcknowledgedState) return;
      this.handleObserverLoss(
        "The Daemon did not acknowledge the pending terminal attachment.",
        this.controllerState.generation,
        false,
      );
    }, RemoteTerminalConnection.PENDING_ATTACHMENT_ACK_TIMEOUT_MS);
  }

  private stopPendingAttachmentAckWatchdog(): void {
    if (this.pendingAttachmentAckTimer !== null) clearTimeout(this.pendingAttachmentAckTimer);
    this.pendingAttachmentAckTimer = null;
  }

  private async reconnectObserver(): Promise<boolean> {
    if (this.closed) return false;
    try {
      const observer = await this.reopenObserver();
      if (!observer) throw new OrniTransportError(
        "UNAVAILABLE",
        "Remote terminal observer was not created.",
        true,
      );
      if (this.closed) {
        observer.close();
        return false;
      }
      this.attachObserver(observer);
      return true;
    } catch {
      return false;
    }
  }

  private applyControllerState(state: RemoteTerminalControllerState): void {
    const generation = state.target.generation;
    // Relay paths may deliver controller state before the joined snapshot.
    // Accept newer generations and reset the revision fence for that target.
    if (generation < this.controllerState.generation) return;
    if (generation > this.controllerState.generation) {
      this.controllerStateEpoch = 0;
      this.controllerStateRevision = 0;
    }
    if (state.epoch < this.controllerStateEpoch
      || (state.epoch === this.controllerStateEpoch && state.revision < this.controllerStateRevision)) return;
    this.controllerStateEpoch = state.epoch;
    this.controllerStateRevision = state.revision;
    const ownerIsLocal = state.owner_attachment_id === this.attachmentId;
    const ownsLease = ownerIsLocal && this.lease !== null;
    this.controlBlockedByOther = !ownerIsLocal && state.owner_attachment_id !== null;
    if (state.owner_attachment_id !== null && !ownerIsLocal && this.lease) {
      this.lease = null;
      this.stopRenewal();
      this.rejectPending(new OrniTransportError("CONFLICT", "Another device now controls this terminal.", true));
    }
    this.updateController({
      // A local owner without a lease proof is not writable yet. It is still
      // safe to show available because this frame is authoritative.
      mode: ownsLease ? "owned" : ownerIsLocal ? "available" : state.owner_attachment_id ? "held-elsewhere" : "available",
      holderLabel: ownsLease || ownerIsLocal ? null : state.owner_attachment_id,
      leaseId: ownsLease ? this.lease?.lease_id ?? null : null,
      expiresAt: ownsLease ? this.lease?.expires_at ?? state.expires_at : state.expires_at,
      generation,
    });
    if (this.controlBlockedByOther) {
      this.controlDesired = false;
      if (this.controlReconnectTimer !== null) clearTimeout(this.controlReconnectTimer);
      this.controlReconnectTimer = null;
    }
  }

  private invalidateLeaseAfterFence(reason: string): void {
    const heldElsewhere = isRemoteTerminalHeldElsewhereReason(reason);
    this.lease = null;
    this.stopRenewal();
    this.controlBlockedByOther = heldElsewhere;
    if (heldElsewhere) {
      this.controlDesired = false;
      if (this.controlReconnectTimer !== null) clearTimeout(this.controlReconnectTimer);
      this.controlReconnectTimer = null;
    }
    this.updateController({
      mode: this.closed ? "unavailable" : heldElsewhere ? "held-elsewhere" : "unavailable",
      holderLabel: heldElsewhere ? this.controllerState.holderLabel : null,
      leaseId: null,
      expiresAt: heldElsewhere ? this.controllerState.expiresAt : null,
      generation: this.controllerState.generation,
    });
  }

  private stopRenewal(): void {
    if (this.renewalTimer !== null) clearTimeout(this.renewalTimer);
    this.renewalTimer = null;
  }

  private invalidateControlForTargetChange(): void {
    // Controller revisions are scoped to one target. A replacement attempt
    // can legitimately restart at a lower revision, so never let the old
    // target's ordering fence suppress its bootstrap state.
    this.controllerStateEpoch = 0;
    this.controllerStateRevision = 0;
    const staleControl = this.control;
    this.control = null;
    this.lease = null;
    this.stopRenewal();
    this.rejectPending(new OrniTransportError(
      "CONFLICT",
      "The terminal process changed while control was active.",
      true,
    ));
    // Input accepted for an old process attempt must never be replayed into a
    // replacement process. A new user action receives a new operation id.
    this.operationJournal.discardAll();
    staleControl?.close();
    this.updateController({
      mode: this.controllerState.mode === "held-elsewhere" ? "held-elsewhere" : "unavailable",
      holderLabel: this.controllerState.mode === "held-elsewhere" ? this.controllerState.holderLabel : null,
      leaseId: null,
      expiresAt: null,
      generation: this.controllerState.generation,
    });
    if (!this.closed && this.controlDesired) this.scheduleControlReconnect();
  }

  private sendJournalOperation(operation: PendingRemoteTerminalOperation): void {
    if (!this.control || !this.lease || !this.writable) return;
    if (operation.inFlight) return;
    if (operation.kind === "input" && operation.bytes) {
      this.control.input(leaseProof(this.lease), operation.bytes, operation.operationId);
    } else if (operation.kind === "resize" && operation.rows && operation.columns) {
      this.control.resize(leaseProof(this.lease), operation.rows, operation.columns, operation.operationId);
    }
    this.operationJournal.markInFlight(operation.operationId);
  }

  private replayPendingOperations(): void {
    const pending = this.operationJournal.eligible();
    for (const operation of pending) {
      try {
        this.sendJournalOperation(operation);
      } catch {
        this.operationJournal.markUnsent();
        break;
      }
    }
  }

  private scheduleOperationReplay(): void {
    if (this.closed || this.operationRetryTimer !== null) return;
    const next = this.operationJournal.nextUnsentRetryAt();
    // Every pending operation is already in flight. Its ACK, error or socket
    // close is the next state transition; polling here used to create a 0ms
    // retry loop while an otherwise healthy relay was processing the input.
    if (next === null) return;
    const delay = Math.max(0, next - Date.now());
    this.operationRetryTimer = setTimeout(() => {
      this.operationRetryTimer = null;
      if (this.closed) return;
      if (this.control && this.lease) this.replayPendingOperations();
      else {
        this.operationJournal.deferUnsent();
        this.scheduleControlReconnect();
      }
      if (this.operationJournal.size > 0) this.scheduleOperationReplay();
    }, delay);
  }

  private scheduleControlReconnect(): void {
    if (this.closed || !this.controlDesired || this.controlBlockedByOther || this.writable
      || this.controlReconnect || this.controlReconnectTimer !== null) return;
    const delay = Math.min(8_000, 250 * (2 ** Math.min(this.controlReconnectAttempt, 5)));
    this.controlReconnectAttempt += 1;
    this.controlReconnectTimer = setTimeout(() => {
      this.controlReconnectTimer = null;
      if (this.closed || !this.controlDesired || this.controlBlockedByOther || this.writable) return;
      const reconnect = this.reconnectControlAndReplay();
      this.controlReconnect = reconnect;
      void reconnect.then((connected) => {
        if (this.controlReconnect !== reconnect) return;
        this.controlReconnect = null;
        if (connected) {
          this.controlReconnectAttempt = 0;
        } else if (!this.closed && this.controlDesired && !this.controlBlockedByOther && !this.writable) {
          // Retry after clearing the in-flight fence. A transient foreground
          // network failure must not leave an otherwise live terminal
          // permanently read-only merely because no input was queued yet.
          this.scheduleControlReconnect();
        }
      });
    }, delay);
  }

  private async reconnectControlAndReplay(): Promise<boolean> {
    try {
      const control = await this.ensureControl();
      // A force acquire is a single explicit user action. Reconnects always
      // use the non-forcing idle/recovery policy.
      await this.requestControl(false);
      if (control === this.control) this.replayPendingOperations();
      return this.writable;
    } catch {
      // Keep the operation durable and retry later. The scheduler owns the
      // bounded backoff so one transient failure cannot recurse or stop the
      // recovery loop.
      this.operationJournal.markUnsent();
      this.operationJournal.deferUnsent();
      this.scheduleOperationReplay();
      return false;
    }
  }

  private resolveInitialSnapshotReadyOnce(): void {
    if (this.initialSnapshotReadySettled) return;
    this.initialSnapshotReadySettled = true;
    this.resolveInitialSnapshotReady();
  }

  private updateController(state: ControllerState): void {
    this.controllerState = state;
    for (const listener of this.controlListeners) listener(this.controller);
  }

  private resetPendingAttachResolution(): void {
    let resolve!: (state: "attached" | "waiting_for_pty") => void;
    let reject!: (cause: Error) => void;
    const promise = new Promise<"attached" | "waiting_for_pty">((accept, fail) => {
      resolve = accept;
      reject = fail;
    });
    void promise.catch(() => undefined);
    this.pendingAttachResolution = { promise, resolve, reject, settled: false };
  }

  private resolvePendingAttachResolution(state: "attached" | "waiting_for_pty"): void {
    if (!this.pendingAttachResolution) this.resetPendingAttachResolution();
    const pending = this.pendingAttachResolution!;
    if (pending.settled) return;
    pending.settled = true;
    pending.resolve(state);
  }

  private rejectPendingAttachResolution(cause: Error): void {
    const pending = this.pendingAttachResolution;
    if (!pending || pending.settled) return;
    pending.settled = true;
    pending.reject(cause);
  }

  private updateStreamState(state: "connecting" | "connected" | "reconnecting" | "closed"): void {
    this.streamState = state;
    for (const listener of this.streamStateListeners) listener(state);
  }

  private resolvePending(): void {
    const pending = this.pendingLease;
    if (!pending) return;
    this.pendingLease = null;
    clearTimeout(pending.timer);
    pending.resolve(this.controller);
  }

  private rejectPending(cause: Error): void {
    const pending = this.pendingLease;
    if (!pending) return;
    this.pendingLease = null;
    clearTimeout(pending.timer);
    pending.reject(cause);
  }
}

/**
 * Runtime boundary for browser/transport implementations. TypeScript's
 * Promise<RemoteTerminalObserver> contract cannot protect us from an older
 * bundle, a malformed adapter, or a compatibility shim resolving undefined.
 * Keep that fault inside the retryable terminal domain instead of allowing a
 * property access to escape as an unclassified JavaScript TypeError.
 */
function requireRemoteTerminalObserver(
  observer: RemoteTerminalObserver | null | undefined,
): RemoteTerminalObserver {
  if (!observer || typeof observer.subscribe !== "function") {
    throw new OrniTransportError(
      "UNAVAILABLE",
      "Remote terminal observer was not created.",
      true,
    );
  }
  return observer;
}

function mapRemotePrompt(prompt: RemoteTerminalPrompt): TerminalPromptEvent {
  return {
    id: prompt.interaction_id,
    title: prompt.title,
    detail: prompt.detail,
    options: prompt.options.map((option) => ({
      id: option.option_id,
      label: option.label,
      ...(option.hotkey ? { hotkey: option.hotkey } : {}),
      ...(option.intent ? { intent: option.intent as TerminalPromptEvent["options"][number]["intent"] } : {}),
    })),
  };
}

function isRecoverableObserverCloseReason(reason: string): boolean {
  // Contract/schema failures indicate an incompatible or corrupt peer. A
  // fresh ticket cannot repair those and an endless reconnect loop leaves the
  // UI stuck on "connecting" without an actionable error.
  if (/contract|schema|invalid (?:json|message|number|text|byte)|unknown remote terminal|different (?:target|stream|control attachment)|sequence gap/i.test(reason)) {
    return false;
  }
  if (/attachment rejected|capacity_exceeded|attachment_timeout|session_not_found|did not acknowledge the pending terminal attachment/i.test(reason)) return false;
  // Transport/source resets are expected during Daemon or relay recovery.
  // A short-lived attempt replacement is also recoverable: the Cloud ticket
  // can land on the old attempt while the Daemon is publishing its new one.
  // `reopenObserver` refreshes the projection and issues a fresh ticket, so
  // treating these runtime messages as terminal strands Mobile on a false
  // failed screen. Expired tickets, policy/auth failures, and an explicitly
  // stopped/closed Session remain terminal.
  return !/(?:ticket|token)\s*(?:expired|invalid)|policy|unauthori[sz]ed|permission|forbidden|session (?:stopped|closed|not found)/i.test(reason);
}

function isRemoteTerminalHeldElsewhereReason(reason: string): boolean {
  return /capacity|busy|another (?:device|attachment)|held elsewhere|controller.*(?:owned|held)/i.test(reason);
}

function isRemoteTerminalLeaseFenceReason(reason: string): boolean {
  return /lease.*(?:fenced|rejected|invalid|expired)|fenced|another (?:device|attachment)|attempt superseded/i.test(reason);
}

function leaseProof(lease: RemoteTerminalControllerLease): RemoteTerminalLeaseProof {
  return {
    lease_id: lease.lease_id,
    lease_epoch: lease.lease_epoch,
    owner_attachment_id: lease.owner_attachment_id,
    target: lease.target,
  };
}

function newClientId(kind: string): string {
  const value = typeof globalThis.crypto?.randomUUID === "function"
    ? globalThis.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${kind}-${value}`;
}

function asError(value: unknown): Error {
  return value instanceof Error ? value : new Error(String(value));
}

/**
 * Development diagnostics for the terminal control single-flight boundary.
 * This intentionally excludes tickets, tokens, cookies, PTY bytes, and
 * message text. Production builds may omit console output at the host level.
 */
function remoteTerminalDiagnostic(event: string, fields: Record<string, unknown>): void {
  if (typeof globalThis.console?.debug !== "function") return;
  try {
    globalThis.console.debug(`[orni.remote-terminal] ${event}`, fields);
  } catch {
    // Diagnostics must never affect terminal control.
  }
}

function linesText(lines: Array<{ cells: Array<{ grapheme: string }> }>): string {
  return lines.map((line) => line.cells.map((cell) => cell.grapheme).join("")).join("\n");
}

function detectApplicationCursorMode(bytes: number[]): boolean | null {
  if (bytes.length === 0) return null;
  const text = new TextDecoder().decode(Uint8Array.from(bytes));
  let mode: boolean | null = null;
  const pattern = /\u001b\[\?([0-9;]+)([hl])/gu;
  for (const match of text.matchAll(pattern)) {
    if (match[1]?.split(";").includes("1")) mode = match[2] === "h";
  }
  return mode;
}

function structuredSnapshotBootstrap(snapshot: RemoteTerminalSnapshot): Uint8Array {
  const lines = snapshot.active_screen === "alternate"
    ? snapshot.alternate ?? []
    : snapshot.primary;
  const chunks = [
    snapshot.active_screen === "alternate" ? "\x1b[?1049h" : "\x1b[?1049l",
    snapshot.modes?.application_cursor ? "\x1b[?1h" : "\x1b[?1l",
    "\x1b[2J\x1b[H",
  ];
  lines.forEach((line, index) => {
    chunks.push(`\x1b[${index + 1};1H`);
    chunks.push(line.cells.map((cell) => cell.grapheme).join(""));
    chunks.push("\x1b[K");
  });
  chunks.push(`\x1b[${snapshot.cursor.row + 1};${snapshot.cursor.column + 1}H`);
  chunks.push(snapshot.cursor.hidden ? "\x1b[?25l" : "\x1b[?25h");
  return new TextEncoder().encode(chunks.join(""));
}

function isEmptyRemoteTerminalSnapshot(snapshot: RemoteTerminalSnapshot): boolean {
  if (snapshot.vt_bootstrap.length > 0 || snapshot.scrollback.length > 0) return false;
  const lines = snapshot.active_screen === "alternate"
    ? snapshot.alternate ?? []
    : snapshot.primary;
  return lines.every((line) => line.cells.every((cell) => cell.grapheme.length === 0));
}
